import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export const ResearchContext = createContext({
  loading: false,
  error: null,
  
  dashboardData: null,
  dashboardLoading: false,
  
  mostViewedData: null,
  mostViewedLoading: false,
  mostViewedFilters: {},
  
  keywordsData: null,
  keywordsLoading: false,
  keywordsViewMode: 'dashboard',
  
  monetizationData: null,
  monetizationLoading: false,
  monetizationViewMode: 'dashboard',
  monetizationFilters: {},
  
  // Новые методы для кэширования
  setCacheData: () => {},
  clearCache: () => {},
  clearAllCaches: () => {},
  isCacheValid: () => false,
  
  setDashboardData: () => {},
  setMostViewedData: () => {},
  setKeywordsData: () => {},
  setMonetizationData: () => {},
  setKeywordsViewMode: () => {},
  setMonetizationViewMode: () => {},
  updateMostViewedFilter: () => {},
  updateMonetizationFilter: () => {},
  clearAllData: () => {}
});

export const ResearchProvider = ({ children }) => {
  const { t } = useTranslation(['research']);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  const [mostViewedData, setMostViewedData] = useState(null);
  const [mostViewedLoading, setMostViewedLoading] = useState(false);
  const [mostViewedFilters, setMostViewedFilters] = useState({
    date_range: '24h',
    region: 'all', 
    category: 'all',
    sort_by: 'views',
    sort_direction: 'desc',
    content_type: 'all',
    min_subscribers: 0,
    min_views: 0,
    max_views: null,
    page: 1,
    page_size: 20
  });

  const [keywordsData, setKeywordsData] = useState(null);
  const [keywordsLoading, setKeywordsLoading] = useState(false);
  const [keywordsViewMode, setKeywordsViewMode] = useState('dashboard');
  const [keywordsFilters, setKeywordsFilters] = useState({
    date_range: '24h',
    region: 'all',
    category: 'all',
    view_type: 'dashboard',
    sort_direction: 'desc',
    min_video_count: 2,
    page: 1,
    page_size: 50
  });

  const [monetizationData, setMonetizationData] = useState(null);
  const [monetizationLoading, setMonetizationLoading] = useState(false);

  const getInitialViewMode = () => {
    if (typeof window !== 'undefined' && window.location.pathname.includes('monetization')) {
      return 'detailed';
    }
    return 'dashboard';
  };

  const initialViewMode = getInitialViewMode();

  const [monetizationViewMode, setMonetizationViewModeState] = useState(initialViewMode);
  const [monetizationFilters, setMonetizationFilters] = useState({
    region: 'all',
    category: 'all',
    sort_by: 'efficiency_12w',
    sort_direction: 'desc',
    trend_filter: 'all',
    view_type: initialViewMode,
    page: 1,
    page_size: 20,
    min_total_views: null,
    max_total_views: null,
    min_subscribers: null,
    max_subscribers: null,
    min_avg_views_per_video: null,
    max_avg_views_per_video: null
  });

  // Состояния для кэширования каждого подраздела
  const [cacheStates, setCacheStates] = useState({
    dashboard: { data: null, filters: null, timestamp: null },
    mostViewed: { data: null, filters: null, timestamp: null },
    keywords: { data: null, filters: null, timestamp: null },
    monetization: { data: null, filters: null, timestamp: null }
  });

  // Конфигурация TTL для каждого подраздела (в миллисекундах)
  const CACHE_TTL_CONFIG = {
    dashboard: 2 * 60 * 60 * 1000,    // 2 часа
    mostViewed: 2 * 60 * 60 * 1000,   // 2 часа 
    keywords: 1 * 60 * 60 * 1000,     // 1 час
    monetization: 2 * 60 * 60 * 1000  // 2 часа
  };
  // Ключи localStorage для каждого подраздела
  const CACHE_KEYS = {
    dashboard: {
      data: 'researchDashboardCache',
      filters: 'researchDashboardFilters', 
      time: 'researchDashboardCacheTime'
    },
    mostViewed: {
      data: 'researchMostViewedCache',
      filters: 'researchMostViewedFilters',
      time: 'researchMostViewedCacheTime'
    },
    keywords: {
      data: 'researchKeywordsCache',
      filters: 'researchKeywordsFilters',
      time: 'researchKeywordsCacheTime'
    },
    monetization: {
      data: 'researchMonetizationCache',
      filters: 'researchMonetizationFilters',
      time: 'researchMonetizationCacheTime'
    }
  };

  // Функция для создания хеша фильтров
  const createFiltersHash = useCallback((filters, section) => {
    if (!filters) return '';
    
    let cleanedFilters = {};
    
    // Разные поля для разных разделов
    if (section === 'dashboard') {
      cleanedFilters = {
        date_range: filters.date_range || '24h',
        region: filters.region || 'all'
      };
    } else if (section === 'mostViewed') {
      cleanedFilters = {
        date_range: filters.date_range || '24h',
        region: filters.region || 'all',
        category: filters.category || 'all',
        sort_by: filters.sort_by || 'views',
        sort_direction: filters.sort_direction || 'desc',
        content_type: filters.content_type || 'all',
        min_subscribers: filters.min_subscribers || 0,
        min_views: filters.min_views || 0,
        max_views: filters.max_views,
        search: filters.search
      };
    } else if (section === 'keywords') {
      cleanedFilters = {
        date_range: filters.date_range || '24h',
        region: filters.region || 'all',
        category: filters.category || 'all',
        view_type: filters.view_type || 'dashboard',
        sort_direction: filters.sort_direction || 'desc',
        min_video_count: filters.min_video_count || 2
      };
    } else if (section === 'monetization') {
      cleanedFilters = {
        region: filters.region || 'all',
        category: filters.category || 'all',
        sort_by: filters.sort_by || 'efficiency_12w',
        sort_direction: filters.sort_direction || 'desc',
        trend_filter: filters.trend_filter || 'all',
        view_type: filters.view_type || 'dashboard',
        min_total_views: filters.min_total_views,
        max_total_views: filters.max_total_views,
        min_subscribers: filters.min_subscribers,
        max_subscribers: filters.max_subscribers,
        min_avg_views_per_video: filters.min_avg_views_per_video,
        max_avg_views_per_video: filters.max_avg_views_per_video
      };
    }
    
    return JSON.stringify(cleanedFilters);
  }, []);

  // Проверка валидности кэша для конкретного подраздела
  const isCacheValid = useCallback((section, customFilters = null) => {
    const cacheState = cacheStates[section];
    if (!cacheState || !cacheState.timestamp || !cacheState.filters) {
      return false;
    }

    // Проверяем время жизни
    const now = Date.now();
    const ttl = CACHE_TTL_CONFIG[section];
    if (now - cacheState.timestamp > ttl) {
      return false;
    }

    // Проверяем совпадение фильтров
    let filtersToCheck = customFilters;
    if (!filtersToCheck) {
      if (section === 'mostViewed') filtersToCheck = mostViewedFilters;
      else if (section === 'keywords') filtersToCheck = keywordsFilters;
      else if (section === 'monetization') filtersToCheck = monetizationFilters;
      else if (section === 'dashboard') filtersToCheck = {};
    }

    const currentFiltersHash = createFiltersHash(filtersToCheck, section);
    const cachedFiltersHash = createFiltersHash(cacheState.filters, section);
    
    return currentFiltersHash === cachedFiltersHash;
  }, [cacheStates, mostViewedFilters, keywordsFilters, monetizationFilters, createFiltersHash]);

  // Сохранение данных в кэш для конкретного подраздела
  const setCacheData = useCallback((section, data, filters = null) => {
    const now = Date.now();
    
    // Определяем фильтры для кэширования
    let filtersToCache = filters;
    if (!filtersToCache) {
      if (section === 'mostViewed') filtersToCache = mostViewedFilters;
      else if (section === 'keywords') filtersToCache = keywordsFilters; 
      else if (section === 'monetization') filtersToCache = monetizationFilters;
      else if (section === 'dashboard') filtersToCache = {};
    }

    // Обновляем состояние кэша
    setCacheStates(prev => ({
      ...prev,
      [section]: {
        data,
        filters: filtersToCache,
        timestamp: now
      }
    }));

    // Сохраняем в localStorage
    try {
      const cacheKeys = CACHE_KEYS[section];
      const cacheData = {
        data,
        timestamp: now,
        filters: filtersToCache
      };
      
      localStorage.setItem(cacheKeys.data, JSON.stringify(cacheData));
      localStorage.setItem(cacheKeys.filters, createFiltersHash(filtersToCache, section));
      localStorage.setItem(cacheKeys.time, now.toString());
    } catch (error) {
      console.warn(`Failed to save ${section} cache to localStorage:`, error);
    }
  }, [mostViewedFilters, keywordsFilters, monetizationFilters, createFiltersHash]);

  // Очистка кэша конкретного подраздела
  const clearCache = useCallback((section) => {
    // Очищаем состояние
    setCacheStates(prev => ({
      ...prev,
      [section]: { data: null, filters: null, timestamp: null }
    }));

    // Очищаем localStorage
    try {
      const cacheKeys = CACHE_KEYS[section];
      localStorage.removeItem(cacheKeys.data);
      localStorage.removeItem(cacheKeys.filters);
      localStorage.removeItem(cacheKeys.time);
    } catch (error) {
      console.warn(`Failed to clear ${section} cache from localStorage:`, error);
    }
  }, []);

  // Очистка всех кэшей
  const clearAllCaches = useCallback(() => {
    // Очищаем все состояния
    setCacheStates({
      dashboard: { data: null, filters: null, timestamp: null },
      mostViewed: { data: null, filters: null, timestamp: null },
      keywords: { data: null, filters: null, timestamp: null },
      monetization: { data: null, filters: null, timestamp: null }
    });

    // Очищаем весь localStorage
    try {
      Object.values(CACHE_KEYS).forEach(keys => {
        localStorage.removeItem(keys.data);
        localStorage.removeItem(keys.filters);
        localStorage.removeItem(keys.time);
      });
    } catch (error) {
      console.warn('Failed to clear all research caches from localStorage:', error);
    }
  }, []);

  // Загрузка кэшей из localStorage при инициализации
  useEffect(() => {
    const loadCachesFromStorage = () => {
      const newCacheStates = {};
      
      Object.entries(CACHE_KEYS).forEach(([section, keys]) => {
        try {
          const cachedData = localStorage.getItem(keys.data);
          const cachedTime = localStorage.getItem(keys.time);
          
          if (cachedData && cachedTime) {
            const parsedData = JSON.parse(cachedData);
            const timestamp = parseInt(cachedTime);
            const now = Date.now();
            const ttl = CACHE_TTL_CONFIG[section];
            
            // Проверяем, не устарел ли кэш
            if (now - timestamp <= ttl && parsedData.data && parsedData.filters) {
              newCacheStates[section] = {
                data: parsedData.data,
                filters: parsedData.filters,
                timestamp: timestamp
              };
              
              // Восстанавливаем данные в соответствующие состояния
              if (section === 'dashboard') {
                setDashboardData(parsedData.data);
              } else if (section === 'mostViewed') {
                setMostViewedData(parsedData.data);
              } else if (section === 'keywords') {
                setKeywordsData(parsedData.data);
              } else if (section === 'monetization') {
                setMonetizationData(parsedData.data);
              }
            } else {
              // Кэш устарел, очищаем
              localStorage.removeItem(keys.data);
              localStorage.removeItem(keys.filters);
              localStorage.removeItem(keys.time);
              newCacheStates[section] = { data: null, filters: null, timestamp: null };
            }
          } else {
            newCacheStates[section] = { data: null, filters: null, timestamp: null };
          }
        } catch (error) {
          console.warn(`Failed to load ${section} cache from localStorage:`, error);
          newCacheStates[section] = { data: null, filters: null, timestamp: null };
        }
      });
      
      setCacheStates(newCacheStates);
    };

    loadCachesFromStorage();
  }, []);

  // Очистка кэша при изменении фильтров в конкретных разделах
  useEffect(() => {
    const cacheState = cacheStates.mostViewed;
    if (cacheState && cacheState.filters) {
      const currentHash = createFiltersHash(mostViewedFilters, 'mostViewed');
      const cachedHash = createFiltersHash(cacheState.filters, 'mostViewed');
      
      if (currentHash !== cachedHash) {
        clearCache('mostViewed');
      }
    }
  }, [mostViewedFilters, cacheStates.mostViewed, createFiltersHash, clearCache]);

  useEffect(() => {
    const cacheState = cacheStates.keywords;
    if (cacheState && cacheState.filters) {
      const currentHash = createFiltersHash(keywordsFilters, 'keywords');
      const cachedHash = createFiltersHash(cacheState.filters, 'keywords');
      
      if (currentHash !== cachedHash) {
        clearCache('keywords');
      }
    }
  }, [keywordsFilters, cacheStates.keywords, createFiltersHash, clearCache]);

  useEffect(() => {
    const cacheState = cacheStates.monetization;
    if (cacheState && cacheState.filters) {
      const currentHash = createFiltersHash(monetizationFilters, 'monetization');
      const cachedHash = createFiltersHash(cacheState.filters, 'monetization');
      
      if (currentHash !== cachedHash) {
        clearCache('monetization');
      }
    }
  }, [monetizationFilters, cacheStates.monetization, createFiltersHash, clearCache]);

  const updateMostViewedFilter = useCallback((filterName, value) => {
    setMostViewedFilters(prevFilters => {
      const newFilters = { ...prevFilters, [filterName]: value };
      
      if (filterName !== 'page' && filterName !== 'page_size') {
        newFilters.page = 1;
      }
      
      return newFilters;
    });
  }, []);

  const updateKeywordsFilter = useCallback((filterName, value) => {
    setKeywordsFilters(prevFilters => {
      const newFilters = { ...prevFilters, [filterName]: value };
      
      if (filterName !== 'page' && filterName !== 'page_size') {
        newFilters.page = 1;
      }
      
      if (filterName === 'view_type') {
        setKeywordsViewMode(value);
      }
      
      return newFilters;
    });
  }, []);

  const updateMonetizationFilter = useCallback((filterName, value) => {
    setMonetizationFilters(prevFilters => {
      const newFilters = { ...prevFilters, [filterName]: value };
      
      if (filterName === 'sort_by' && value === 'trend_filter') {
        newFilters.sort_by = 'efficiency_12w';
      }

      if (filterName === 'trend_filter') {
        const validTrendFilters = ['all', 'growing', 'mixed', 'declining'];
        if (!validTrendFilters.includes(value)) {
          newFilters.trend_filter = 'all';
        }
      }
      
      if (filterName !== 'page' && filterName !== 'page_size') {
        newFilters.page = 1;
      }
      
      if (filterName === 'view_type') {
        setMonetizationViewModeState(value);
      }
      
      return newFilters;
    });
  }, []);

  const updateKeywordsViewMode = useCallback((viewMode) => {
    setKeywordsViewMode(viewMode);
    updateKeywordsFilter('view_type', viewMode);
  }, [updateKeywordsFilter]);

  const setMonetizationViewMode = useCallback((viewMode) => {
    setMonetizationViewModeState(viewMode);
    
    setMonetizationFilters(prevFilters => {
      const newFilters = { 
        ...prevFilters, 
        view_type: viewMode,
        page: 1
      };
      return newFilters;
    });
    
    setMonetizationData(null);
  }, []);

  const updateMonetizationViewMode = useCallback((viewMode) => {
    setMonetizationViewMode(viewMode);
  }, [setMonetizationViewMode]);

  const clearAllData = useCallback(() => {
    setDashboardData(null);
    setMostViewedData(null);
    setKeywordsData(null);
    setMonetizationData(null);
    setError(null);
    clearAllCaches();
  }, [clearAllCaches]);

  const setGlobalError = useCallback((error) => {
    setError(error);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const isDataStale = useCallback((timestamp, maxAgeMinutes = 5) => {
    if (!timestamp) return true;
    const now = new Date().getTime();
    const dataAge = now - timestamp;
    return dataAge > (maxAgeMinutes * 60 * 1000);
  }, []);

  const resetMostViewedFilters = useCallback(() => {
    setMostViewedFilters({
      date_range: '24h',
      region: 'all',
      category: 'all', 
      sort_by: 'views',
      sort_direction: 'desc',
      content_type: 'all',
      min_subscribers: 0,
      min_views: 0,
      max_views: null,
      page: 1,
      page_size: 20
    });
  }, []);

  const resetKeywordsFilters = useCallback(() => {
    setKeywordsFilters({
      date_range: '24h',
      region: 'all',
      category: 'all',
      view_type: 'dashboard',
      sort_direction: 'desc',
      min_video_count: 2,
      page: 1,
      page_size: 50
    });
    setKeywordsViewMode('dashboard');
  }, []);

  const resetMonetizationFilters = useCallback(() => {
    const currentViewType = monetizationViewMode || 'detailed';
    
    setMonetizationFilters({
      region: 'all',
      category: 'all',
      sort_by: 'efficiency_12w',
      sort_direction: 'desc',
      trend_filter: 'all',
      view_type: currentViewType,
      page: 1,
      page_size: 20,
      min_total_views: null,
      max_total_views: null,
      min_subscribers: null,
      max_subscribers: null,
      min_avg_views_per_video: null,
      max_avg_views_per_video: null
    });
  }, [monetizationViewMode]);

  const getChannelSizeFilters = useCallback(() => {
    return {
      min_total_views: monetizationFilters.min_total_views,
      max_total_views: monetizationFilters.max_total_views,
      min_subscribers: monetizationFilters.min_subscribers,
      max_subscribers: monetizationFilters.max_subscribers,
      min_avg_views_per_video: monetizationFilters.min_avg_views_per_video,
      max_avg_views_per_video: monetizationFilters.max_avg_views_per_video
    };
  }, [monetizationFilters]);

  const hasActiveChannelSizeFilters = useCallback(() => {
    const filters = getChannelSizeFilters();
    return !!(
      filters.min_total_views || 
      filters.max_total_views || 
      filters.min_subscribers || 
      filters.max_subscribers || 
      filters.min_avg_views_per_video || 
      filters.max_avg_views_per_video
    );
  }, [getChannelSizeFilters]);

  const getDebugInfo = useCallback(() => {
    const isSynced = monetizationViewMode === monetizationFilters.view_type;

    return {
      dashboardData: !!dashboardData,
      mostViewedData: !!mostViewedData,
      keywordsData: !!keywordsData,
      monetizationData: !!monetizationData,
      mostViewedFilters,
      keywordsFilters,
      monetizationFilters,
      keywordsViewMode,
      monetizationViewMode,
      isViewModeSynced: isSynced,
      syncStatus: {
        viewMode: monetizationViewMode,
        filterViewType: monetizationFilters.view_type,
        isSynced: isSynced
      },
      channelSizeFilters: getChannelSizeFilters(),
      hasActiveChannelSizeFilters: hasActiveChannelSizeFilters(),
      trendFilterValidation: {
        current: monetizationFilters.trend_filter,
        isValid: ['all', 'growing', 'mixed', 'declining'].includes(monetizationFilters.trend_filter)
      },
      loading: {
        global: loading,
        dashboard: dashboardLoading,
        mostViewed: mostViewedLoading,
        keywords: keywordsLoading,
        monetization: monetizationLoading
      },
      error: error ? error.message : null,
      cacheStates: {
        dashboard: {
          hasData: !!cacheStates.dashboard.data,
          timestamp: cacheStates.dashboard.timestamp,
          isValid: isCacheValid('dashboard')
        },
        mostViewed: {
          hasData: !!cacheStates.mostViewed.data,
          timestamp: cacheStates.mostViewed.timestamp,
          isValid: isCacheValid('mostViewed')
        },
        keywords: {
          hasData: !!cacheStates.keywords.data,
          timestamp: cacheStates.keywords.timestamp,
          isValid: isCacheValid('keywords')
        },
        monetization: {
          hasData: !!cacheStates.monetization.data,
          timestamp: cacheStates.monetization.timestamp,
          isValid: isCacheValid('monetization')
        }
      }
    };
  }, [
    dashboardData, 
    mostViewedData, 
    keywordsData, 
    monetizationData,
    mostViewedFilters, 
    keywordsFilters,
    monetizationFilters,
    keywordsViewMode,
    monetizationViewMode,
    getChannelSizeFilters,
    hasActiveChannelSizeFilters,
    loading,
    dashboardLoading,
    mostViewedLoading,
    keywordsLoading,
    monetizationLoading,
    error,
    cacheStates,
    isCacheValid
  ]);

  const value = React.useMemo(() => ({
    loading,
    error,
    setLoading,
    setError: setGlobalError,
    clearError,

    dashboardData,
    dashboardLoading,
    setDashboardData,
    setDashboardLoading,

    mostViewedData,
    mostViewedLoading, 
    mostViewedFilters,
    setMostViewedData,
    setMostViewedLoading,
    updateMostViewedFilter,
    resetMostViewedFilters,

    keywordsData,
    keywordsLoading,
    keywordsViewMode,
    keywordsFilters,
    setKeywordsData,
    setKeywordsLoading,
    setKeywordsViewMode: updateKeywordsViewMode,
    updateKeywordsFilter,
    resetKeywordsFilters,

    monetizationData,
    monetizationLoading,
    monetizationViewMode,
    monetizationFilters,
    setMonetizationData,
    setMonetizationLoading,
    setMonetizationViewMode,
    updateMonetizationFilter,
    resetMonetizationFilters,
    
    getChannelSizeFilters,
    hasActiveChannelSizeFilters,

    // Новые методы кэширования
    setCacheData,
    clearCache,
    clearAllCaches,
    isCacheValid,
    cacheStates,

    clearAllData,
    isDataStale,
    getDebugInfo
  }), [
    loading,
    error,
    setGlobalError,
    clearError,
    dashboardData,
    dashboardLoading,
    setDashboardData,
    setDashboardLoading,
    mostViewedData,
    mostViewedLoading,
    mostViewedFilters,
    setMostViewedData,
    setMostViewedLoading,
    updateMostViewedFilter,
    resetMostViewedFilters,
    keywordsData,
    keywordsLoading,
    keywordsViewMode,
    keywordsFilters,
    setKeywordsData,
    setKeywordsLoading,
    updateKeywordsViewMode,
    updateKeywordsFilter,
    resetKeywordsFilters,
    monetizationData,
    monetizationLoading,
    monetizationViewMode,
    monetizationFilters,
    setMonetizationData,
    setMonetizationLoading,
    setMonetizationViewMode,
    updateMonetizationFilter,
    resetMonetizationFilters,
    getChannelSizeFilters,
    hasActiveChannelSizeFilters,
    setCacheData,
    clearCache,
    clearAllCaches,
    isCacheValid,
    cacheStates,
    clearAllData,
    isDataStale,
    getDebugInfo
  ]);

  return (
    <ResearchContext.Provider value={value}>
      {children}
    </ResearchContext.Provider>
  );
};

export const useResearchContext = () => {
  const context = useContext(ResearchContext);
  
  if (!context) {
    throw new Error('useResearchContext must be used within a ResearchProvider');
  }
  
  return context;
};

export default ResearchProvider;